'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { ArrowCircleLeft, ArrowCircleRight } from 'iconsax-react';
import { cn } from './helpers';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex h-9 border-b items-center w-full pb-4 border-primary-200 text-gray-600',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center cursor-pointer justify-center whitespace-nowrap px-3 py-4 text-sm font-medium ring-primary-200 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-b-2 data-[state=active]:border-primary-1000 border-b border-primary-200 data-[state=active]:text-primary-1000',
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-primary-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

/**
 * Horizontal space reserved on each edge of the strip so a tab never comes to
 * rest underneath one of the overlaid arrow buttons.
 */
const ARROW_GUTTER = 24;

/** Pointer travel (px) past which a drag stops counting as a click. */
const DRAG_THRESHOLD = 5;

/**
 * A `TabsList` that scrolls horizontally when its triggers overflow: drag to
 * pan, or use the circle arrows that fade in over each scrollable edge. The
 * active trigger (`data-state="active"`) is kept in view.
 */
const TabsCarousel = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const draggedRef = React.useRef(false);
  const activeRef = React.useRef<Element | null>(null);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const syncEdges = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 1px slack: fractional layout widths otherwise leave an arrow enabled at
    // the very end of travel.
    setCanScrollPrev(el.scrollLeft > 1);
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    syncEdges();
    const observer = new ResizeObserver(syncEdges);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);

    return () => observer.disconnect();
  }, [syncEdges]);

  // Runs after every render so a change of active tab (driven by the URL in the
  // consumer) brings that tab into view. Scrolling only happens when the active
  // element actually changed and is not already fully visible.
  React.useEffect(() => {
    const el = scrollRef.current;
    const active = el?.querySelector('[data-state="active"]') as HTMLElement | null;
    if (!el || !active || active === activeRef.current) return;

    const isFirst = activeRef.current === null;
    activeRef.current = active;

    const start = active.offsetLeft;
    const end = start + active.offsetWidth;
    let left = el.scrollLeft;

    if (start - ARROW_GUTTER < el.scrollLeft) {
      left = start - ARROW_GUTTER;
    } else if (end + ARROW_GUTTER > el.scrollLeft + el.clientWidth) {
      left = end + ARROW_GUTTER - el.clientWidth;
    }

    if (left !== el.scrollLeft) {
      el.scrollTo({ left, behavior: isFirst ? 'auto' : 'smooth' });
    }
  });

  /**
   * Scrolls to the nearest edge that leaves the first clipped tab in that
   * direction fully visible, so a click never parks a label half-cut.
   */
  const scrollByTab = (direction: 'prev' | 'next') => {
    const el = scrollRef.current;
    if (!el) return;

    const tabs = Array.from(
      el.firstElementChild?.children ?? []
    ) as HTMLElement[];
    const viewStart = el.scrollLeft;
    const viewEnd = viewStart + el.clientWidth;

    if (direction === 'next') {
      const tab = tabs.find((t) => t.offsetLeft + t.offsetWidth > viewEnd - 1);
      const left = tab
        ? tab.offsetLeft + tab.offsetWidth + ARROW_GUTTER - el.clientWidth
        : el.scrollWidth;
      el.scrollTo({ left, behavior: 'smooth' });
      return;
    }

    const tab = [...tabs].reverse().find((t) => t.offsetLeft < viewStart + 1);
    el.scrollTo({
      left: tab ? tab.offsetLeft - ARROW_GUTTER : 0,
      behavior: 'smooth',
    });
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    // Touch and pen already pan the scroll container natively; handling them
    // here too would move it twice as far.
    if (!el || event.button !== 0 || event.pointerType !== 'mouse') return;

    // Reset here rather than on click, so a drag that ends off a trigger (no
    // click fires) cannot swallow the following genuine click.
    draggedRef.current = false;

    const startX = event.clientX;
    const startScroll = el.scrollLeft;

    const handleMove = (moveEvent: PointerEvent) => {
      const delta = moveEvent.clientX - startX;
      if (Math.abs(delta) > DRAG_THRESHOLD) draggedRef.current = true;
      el.scrollLeft = startScroll - delta;
    };

    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  const isScrollable = canScrollPrev || canScrollNext;

  return (
    <div className='relative border-b border-primary-200'>
      <div
        ref={scrollRef}
        onScroll={syncEdges}
        onPointerDown={handlePointerDown}
        onClickCapture={(event) => {
          if (!draggedRef.current) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        // Kills the browser's native link-ghost when dragging across triggers.
        onDragStart={(event) => event.preventDefault()}
        className={cn(
          'relative overflow-x-auto scrollbar-none select-none',
          isScrollable && 'cursor-grab active:cursor-grabbing'
        )}
      >
        <TabsPrimitive.List
          ref={ref}
          className={cn(
            'inline-flex h-16 w-max min-w-full items-stretch px-6 text-gray-600',
            '[&>*]:-mb-px [&>*]:border-b-0',
            '[&>*[data-state=active]]:border-b-2 [&>*[data-state=active]]:border-primary-1000',
            className
          )}
          {...props}
        >
          {children}
        </TabsPrimitive.List>
      </div>

      <TabsCarouselArrow
        direction='prev'
        visible={canScrollPrev}
        onClick={() => scrollByTab('prev')}
      />
      <TabsCarouselArrow
        direction='next'
        visible={canScrollNext}
        onClick={() => scrollByTab('next')}
      />
    </div>
  );
});
TabsCarousel.displayName = 'TabsCarousel';

function TabsCarouselArrow({
  direction,
  visible,
  onClick,
}: {
  direction: 'prev' | 'next';
  visible: boolean;
  onClick: () => void;
}) {
  const isPrev = direction === 'prev';
  const Icon = isPrev ? ArrowCircleLeft : ArrowCircleRight;

  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-y-0 flex w-16 items-center transition-opacity',
        isPrev
          ? 'left-0 justify-start bg-linear-to-r from-white from-50% to-transparent'
          : 'right-0 justify-end bg-linear-to-l from-white from-50% to-transparent',
        visible ? 'opacity-100' : 'opacity-0'
      )}
    >
      <button
        type='button'
        tabIndex={-1}
        onClick={onClick}
        disabled={!visible}
        className={cn(
          'pointer-events-auto mx-2 cursor-pointer text-gray-600 transition-colors hover:text-primary-1000',
          !visible && 'pointer-events-none'
        )}
      >
        <Icon size={20} />
      </button>
    </div>
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent, TabsCarousel };
